import { tz } from '@date-fns/tz';

import { IDEALIST_TIME_ZONE } from '#/utils/constants/general/idealistTimeZone';

import { normalizeDateInput, type DateInput } from './domain';

// Idealist/New York business-time helpers.
//
// These helpers accept InstantInput and produce values whose wall-clock fields
// intentionally match Idealist time rather than the caller's local timezone.
export function getIdealistDateFnsTimeZone() {
  return tz(IDEALIST_TIME_ZONE);
}

function getIdealistDateTimeParts(date: DateInput) {
  const formatterOptions = {
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: IDEALIST_TIME_ZONE,
  } satisfies Intl.DateTimeFormatOptions;

  const formattedDateParts = new Intl.DateTimeFormat(
    'en-us',
    formatterOptions,
  ).formatToParts(normalizeDateInput(date));

  const dateTimeParts = formattedDateParts.reduce<{
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
  } | null>((parts, part) => {
    if (
      part.type !== 'year' &&
      part.type !== 'month' &&
      part.type !== 'day' &&
      part.type !== 'hour' &&
      part.type !== 'minute' &&
      part.type !== 'second'
    ) {
      return parts;
    }

    return {
      ...(parts ?? {
        year: 0,
        month: 0,
        day: 0,
        hour: 0,
        minute: 0,
        second: 0,
      }),
      [part.type]: parseInt(part.value, 10),
    };
  }, null);

  if (!dateTimeParts) {
    throw new Error('Unable to parse Idealist datetime parts');
  }

  return dateTimeParts;
}

export function convertInstantToIdealistWallClockDate(date: DateInput): Date {
  const { year, month, day, hour, minute, second } =
    getIdealistDateTimeParts(date);

  return new Date(year, month - 1, day, hour === 24 ? 0 : hour, minute, second);
}
